/**
 * Parse published Google Sheet CSV (or any CSV) into guest-shaped rows.
 * Expect a header row with columns like email, firstName, group, siteVisibility, rsvpStatus (aliases below).
 */

import {
  GUEST_POOL,
  GUEST_SIDE,
  VERBAL_RSVP,
  computeRsvpStatusFromForm,
  isValidEmailFormat,
  isValidGuestRow,
  legacyFormRsvpFromRsvpStatus,
  normalizeEmail,
  looksLikePostalAddress,
  normalizeGuestRow,
  normalizeGuestSide,
  RSVP_STATUSES,
  SITE_VISIBILITY,
} from './guestAllowlist';

/** RFC-style CSV parse with quoted fields and doubled quotes. */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let i = 0;
  let inQuotes = false;
  const s = String(text || '').replace(/^\uFEFF/, '');
  while (i < s.length) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += c;
      i += 1;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (c === ',') {
      row.push(cell);
      cell = '';
      i += 1;
      continue;
    }
    if (c === '\r') {
      i += 1;
      continue;
    }
    if (c === '\n') {
      row.push(cell);
      if (row.some((x) => String(x).trim() !== '')) rows.push(row);
      row = [];
      cell = '';
      i += 1;
      continue;
    }
    cell += c;
    i += 1;
  }
  row.push(cell);
  if (row.some((x) => String(x).trim() !== '')) rows.push(row);
  return rows;
}

function normHeader(h) {
  return String(h || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function colIndex(headers, ...aliases) {
  const n = headers.map(normHeader);
  for (const a of aliases) {
    const key = normHeader(a);
    const i = n.indexOf(key);
    if (i >= 0) return i;
  }
  return -1;
}

function findHeaderIndex(headers, predicate) {
  return headers.findIndex((h) => predicate(String(h || '')));
}

function parseSiteVisibility(raw) {
  const v = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (
    v === SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK ||
    v === 'ceremony_reception_penicuik' ||
    v === 'ceremony+penicuik' ||
    v === 'ceremony_penicuik' ||
    v === 'penicuik' ||
    v === 'penicuik_rooms' ||
    v === 'staying_at_penicuik' ||
    v === 'on_site' ||
    v === 'onsite' ||
    v === 'rooms_at_penicuik'
  ) {
    return SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
  }
  if (
    v === SITE_VISIBILITY.CEREMONY_RECEPTION ||
    v === 'ceremony_reception' ||
    v === 'ceremony+reception' ||
    v === 'ceremony' ||
    v === 'full' ||
    v === 'all' ||
    v === 'yes'
  ) {
    return SITE_VISIBILITY.CEREMONY_RECEPTION;
  }
  if (
    v === SITE_VISIBILITY.RECEPTION_ONLY ||
    v === 'reception_only' ||
    v === 'reception' ||
    v === 'evening' ||
    v === 'evening_only'
  ) {
    return SITE_VISIBILITY.RECEPTION_ONLY;
  }
  return null;
}

function penicuikOnSiteCellYes(raw) {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  return v === 'y' || v === 'yes' || v === 'true' || v === '1';
}

function parseRsvpStatus(raw) {
  const v = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
  if (!v) return null;
  if (v === RSVP_STATUSES.NOT_SENT || v === 'not_sent' || v === 'notsent' || v === '-') return RSVP_STATUSES.NOT_SENT;
  if (v === RSVP_STATUSES.PENDING || v === 'pending' || v === 'awaiting') return RSVP_STATUSES.PENDING;
  if (
    v === RSVP_STATUSES.CONFIRMED ||
    v === 'confirmed' ||
    v === 'yes' ||
    v === 'attending' ||
    v === 'accept'
  ) {
    return RSVP_STATUSES.CONFIRMED;
  }
  if (v === RSVP_STATUSES.DECLINED || v === 'declined' || v === 'no' || v === 'decline' || v === 'unable') {
    return RSVP_STATUSES.DECLINED;
  }
  return null;
}

/** Wedding template "Tier" column: Definite / Uncertain etc. */
function inferRsvpFromTier(tierRaw) {
  const t = String(tierRaw || '')
    .trim()
    .toLowerCase();
  if (t === 'uncertain') return RSVP_STATUSES.PENDING;
  return null;
}

function looksLikeEmailField(s) {
  return /[^\s@]+@[^\s@]+\.[^\s@]+/.test(String(s || ''));
}

function invitedColumnMeansSkip(raw) {
  const v = String(raw || '')
    .trim()
    .toLowerCase();
  return v === 'n' || v === 'no';
}

/**
 * @returns {{ guests: Array<{id?: string, email: string, firstName: string, group: string, siteVisibility: string, rsvpStatus: string, rsvpUpdatedAt: string|null}>, skipped: number, warnings: string[] }}
 */
export function guestsFromCsvText(csvText) {
  const rows = parseCsv(csvText);
  if (rows.length < 2) {
    return { guests: [], skipped: 0, warnings: ['CSV needs a header row and at least one data row.'] };
  }
  const headers = rows[0];
  const iEmail = colIndex(headers, 'email', 'e-mail', 'email_address');
  const iFirst = colIndex(
    headers,
    'first_name',
    'firstname',
    'first',
    'name',
    'guest',
  );
  const iLast = colIndex(headers, 'last_name', 'lastname', 'surname', 'family_name');
  let iHousehold = colIndex(
    headers,
    'household_group',
    'household__group',
    'householdgroup',
    'household',
  );
  if (iHousehold < 0) {
    iHousehold = findHeaderIndex(
      headers,
      (h) => h.toLowerCase().includes('household') && h.toLowerCase().includes('group'),
    );
  }
  let iSide = colIndex(headers, 'side_ben_meg', 'sidebenmeg');
  if (iSide < 0) {
    iSide = findHeaderIndex(headers, (h) => {
      const l = h.toLowerCase();
      return l.includes('side') && (l.includes('ben') || l.includes('meg'));
    });
  }
  const iInvited = colIndex(headers, 'invited_yn', 'invitedyn', 'invited');
  const iTier = colIndex(headers, 'tier');
  const iGroup = colIndex(headers, 'group', 'party', 'table');
  const iVis = colIndex(headers, 'sitevisibility', 'site_visibility', 'visibility', 'invite', 'ceremony');
  let iPenicuikOnSite = colIndex(
    headers,
    'penicuikonsite',
    'penicuik_on_site',
    'staying_at_penicuik',
    'on_site_penicuik',
    'penicuik_rooms',
    'rooms_at_penicuik',
  );
  if (iPenicuikOnSite < 0) {
    iPenicuikOnSite = findHeaderIndex(headers, (h) => {
      const l = normHeader(h);
      if (!l.includes('penicuik') || l.includes('email')) return false;
      return (
        l.includes('onsite') ||
        l.includes('rooms') ||
        l.endsWith('yn') ||
        l === 'penicuik'
      );
    });
  }
  const iRsvp = colIndex(headers, 'rsvpstatus', 'rsvp_status', 'rsvp', 'status');
  const iId = colIndex(headers, 'id', 'guest_id', 'guestid');
  const iRsvpAt = colIndex(headers, 'rsvpupdatedat', 'rsvp_updated_at', 'rsvp_date', 'updated');
  const iPhone = colIndex(headers, 'phone', 'mobile', 'telephone', 'tel');
  const iNotes = colIndex(headers, 'notes', 'note');
  let iDietary = colIndex(headers, 'dietary_requirements', 'dietaryrequirements', 'dietary', 'diet');
  if (iDietary < 0) {
    iDietary = findHeaderIndex(headers, (h) => h.toLowerCase().includes('dietary'));
  }
  let iAddress = colIndex(headers, 'address', 'postal_address', 'postaladdress', 'street');
  if (iAddress < 0) {
    iAddress = findHeaderIndex(headers, (h) => {
      const l = h.toLowerCase();
      return (l.includes('address') || l.includes('postal')) && !l.includes('email');
    });
  }

  if (iEmail < 0) {
    return {
      guests: [],
      skipped: 0,
      warnings: ['No email column found. Use a header named email (or e-mail).'],
    };
  }

  const guests = [];
  let skipped = 0;
  const warnings = [];

  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r];
    if (iInvited >= 0 && invitedColumnMeansSkip(row[iInvited])) {
      skipped += 1;
      continue;
    }

    const emailRaw = row[iEmail] != null ? String(row[iEmail]) : '';
    const householdRaw = iHousehold >= 0 && row[iHousehold] != null ? String(row[iHousehold]).trim() : '';
    let emailFromHousehold = false;
    let email = normalizeEmail(emailRaw);
    if (!isValidEmailFormat(email) && householdRaw && looksLikeEmailField(householdRaw)) {
      email = normalizeEmail(householdRaw);
      emailFromHousehold = true;
    }
    if (!isValidEmailFormat(email)) {
      skipped += 1;
      continue;
    }

    let firstName = iFirst >= 0 && row[iFirst] != null ? String(row[iFirst]).trim() : '';
    const lastName = iLast >= 0 && row[iLast] != null ? String(row[iLast]).trim() : '';
    if (!firstName && lastName) firstName = lastName;

    let group = '';
    let addressFromHousehold = false;
    if (!emailFromHousehold && householdRaw) {
      if (looksLikePostalAddress(householdRaw)) {
        addressFromHousehold = true;
      } else {
        group = householdRaw;
      }
    }
    if (!group && iGroup >= 0 && row[iGroup] != null) {
      group = String(row[iGroup]).trim();
    }
    const side = iSide >= 0 && row[iSide] != null ? String(row[iSide]).trim() : '';
    const guestSide = normalizeGuestSide(side);
    if (emailFromHousehold) {
      group = side || '';
    } else if (side) {
      group = group ? `${side} — ${group}` : side;
    }

    const dietary = iDietary >= 0 && row[iDietary] != null ? String(row[iDietary]).trim() : '';
    const notes = iNotes >= 0 && row[iNotes] != null ? String(row[iNotes]).trim() : '';
    let address = iAddress >= 0 && row[iAddress] != null ? String(row[iAddress]).trim() : '';
    if (addressFromHousehold) {
      address = address && address !== householdRaw.trim() ? `${address}\n${householdRaw}`.trim() : householdRaw;
    }
    const phone = iPhone >= 0 && row[iPhone] != null ? String(row[iPhone]).trim() : '';

    const id = iId >= 0 && row[iId] != null && String(row[iId]).trim() ? String(row[iId]).trim() : undefined;

    let siteVisibility = SITE_VISIBILITY.RECEPTION_ONLY;
    if (iVis >= 0 && row[iVis] != null && String(row[iVis]).trim()) {
      const parsed = parseSiteVisibility(row[iVis]);
      if (parsed) siteVisibility = parsed;
      else warnings.push(`Row ${r + 1}: unknown site visibility "${row[iVis]}", used reception only.`);
    }
    if (iPenicuikOnSite >= 0 && row[iPenicuikOnSite] != null && penicuikOnSiteCellYes(row[iPenicuikOnSite])) {
      siteVisibility = SITE_VISIBILITY.CEREMONY_RECEPTION_PENICUIK;
    }

    let rsvpStatus = RSVP_STATUSES.NOT_SENT;
    let rsvpRaw = iRsvp >= 0 && row[iRsvp] != null ? String(row[iRsvp]).trim() : '';
    if (rsvpRaw && (looksLikeEmailField(rsvpRaw) || rsvpRaw.length > 80)) {
      warnings.push(
        `Row ${r + 1}: RSVP column looks like notes or an address; ignored for RSVP. Fix column alignment in the sheet if needed.`,
      );
      rsvpRaw = '';
    }
    if (rsvpRaw) {
      const parsed = parseRsvpStatus(rsvpRaw);
      if (parsed) rsvpStatus = parsed;
      else warnings.push(`Row ${r + 1}: unknown RSVP "${rsvpRaw}", used not sent or tier hint.`);
    }
    if (rsvpStatus === RSVP_STATUSES.NOT_SENT && iTier >= 0) {
      const fromTier = inferRsvpFromTier(row[iTier]);
      if (fromTier) rsvpStatus = fromTier;
    }

    let rsvpUpdatedAt = null;
    if (iRsvpAt >= 0 && row[iRsvpAt] != null && String(row[iRsvpAt]).trim()) {
      const t = Date.parse(String(row[iRsvpAt]).trim());
      rsvpUpdatedAt = Number.isNaN(t) ? null : new Date(t).toISOString();
    }

    const formRsvp = legacyFormRsvpFromRsvpStatus(rsvpStatus);

    guests.push({
      id,
      email,
      firstName,
      lastName,
      group,
      guestSide,
      dietary,
      accommodation: '',
      coachIn: null,
      coachOut: null,
      notes,
      address,
      phone,
      siteVisibility,
      pool: GUEST_POOL.INVITED,
      verbalRsvp: VERBAL_RSVP.UNSET,
      formRsvp,
      isGroupLead: false,
      sortOrder: guests.length,
      rsvpStatus,
      rsvpUpdatedAt,
    });
  }

  return { guests, skipped, warnings };
}

/** Merge sheet rows into the current registry: match by id first, then email; add new rows. */
export function mergeRegistryWithImportedGuests(current, imported, newId) {
  const next = current.map((g) => ({ ...g }));
  const idxByEmail = new Map();
  const idxById = new Map();
  next.forEach((g, i) => {
    idxByEmail.set(normalizeEmail(g.email), i);
    idxById.set(g.id, i);
  });

  for (const row of imported) {
    const email = normalizeEmail(row.email);

    let idx;
    if (row.id && idxById.has(row.id)) idx = idxById.get(row.id);
    else if (email && idxByEmail.has(email)) idx = idxByEmail.get(email);
    else idx = undefined;

    if (idx !== undefined) {
      const g = next[idx];
      let formRsvp = row.formRsvp !== undefined ? row.formRsvp : g.formRsvp;
      if (row.formRsvp === undefined && row.rsvpStatus !== undefined && row.rsvpStatus !== g.rsvpStatus) {
        formRsvp = legacyFormRsvpFromRsvpStatus(row.rsvpStatus);
      }
      const rsvpStatus = computeRsvpStatusFromForm(formRsvp);
      let rsvpUpdatedAt = g.rsvpUpdatedAt;
      if (row.formRsvp !== undefined && row.formRsvp !== g.formRsvp) {
        rsvpUpdatedAt = new Date().toISOString();
      } else if (
        row.formRsvp === undefined &&
        row.rsvpStatus !== undefined &&
        row.rsvpStatus !== g.rsvpStatus
      ) {
        rsvpUpdatedAt = row.rsvpUpdatedAt || new Date().toISOString();
      } else if (row.rsvpUpdatedAt != null) {
        rsvpUpdatedAt = row.rsvpUpdatedAt;
      }

      next[idx] = {
        ...g,
        email: email || g.email,
        firstName: row.firstName !== '' ? row.firstName : g.firstName,
        lastName: row.lastName !== undefined && row.lastName !== '' ? row.lastName : g.lastName ?? '',
        group: row.group !== undefined && row.group !== null ? String(row.group) : g.group,
        guestSide: row.guestSide !== undefined ? row.guestSide : g.guestSide ?? GUEST_SIDE.UNSPECIFIED,
        dietary: row.dietary !== undefined ? row.dietary : g.dietary,
        accommodation: row.accommodation !== undefined ? row.accommodation : g.accommodation,
        coachIn: row.coachIn !== undefined ? row.coachIn : g.coachIn,
        coachOut: row.coachOut !== undefined ? row.coachOut : g.coachOut,
        notes: row.notes !== undefined ? row.notes : g.notes,
        address: row.address !== undefined ? row.address : g.address,
        phone: row.phone !== undefined ? row.phone : g.phone,
        siteVisibility: row.siteVisibility,
        pool: row.pool !== undefined ? row.pool : g.pool,
        verbalRsvp: row.verbalRsvp !== undefined ? row.verbalRsvp : g.verbalRsvp,
        formRsvp,
        isGroupLead: row.isGroupLead !== undefined ? row.isGroupLead : g.isGroupLead,
        sortOrder: row.sortOrder !== undefined ? row.sortOrder : g.sortOrder,
        plusOnes: row.plusOnes !== undefined ? row.plusOnes : g.plusOnes ?? 0,
        tags: row.tags !== undefined ? row.tags : g.tags ?? [],
        rsvpStatus,
        rsvpUpdatedAt,
      };
    } else {
      const id = row.id && !idxById.has(row.id) ? row.id : newId();
      const ng = {
        id,
        email,
        firstName: row.firstName || '',
        lastName: row.lastName ?? '',
        group: row.group || '',
        guestSide: row.guestSide ?? GUEST_SIDE.UNSPECIFIED,
        dietary: row.dietary ?? '',
        accommodation: row.accommodation ?? '',
        coachIn: row.coachIn ?? null,
        coachOut: row.coachOut ?? null,
        notes: row.notes ?? '',
        address: row.address ?? '',
        phone: row.phone ?? '',
        siteVisibility: row.siteVisibility,
        pool: row.pool ?? GUEST_POOL.INVITED,
        verbalRsvp: row.verbalRsvp ?? VERBAL_RSVP.UNSET,
        formRsvp: row.formRsvp ?? legacyFormRsvpFromRsvpStatus(row.rsvpStatus),
        isGroupLead: row.isGroupLead ?? false,
        sortOrder: row.sortOrder ?? next.length,
        plusOnes: row.plusOnes ?? 0,
        tags: row.tags ?? [],
        rsvpStatus: row.rsvpStatus,
        rsvpUpdatedAt:
          row.rsvpUpdatedAt ??
          (row.rsvpStatus !== RSVP_STATUSES.NOT_SENT ? new Date().toISOString() : null),
      };
      ng.rsvpStatus = computeRsvpStatusFromForm(ng.formRsvp);
      next.push(ng);
      if (email) idxByEmail.set(email, next.length - 1);
      idxById.set(id, next.length - 1);
    }
  }

  return next.map(normalizeGuestRow).filter(isValidGuestRow);
}
